import { useEffect, useState, useSyncExternalStore } from 'react';

import {
  populationFingerprint,
  DEFAULT_EXTRACT_POPULATION,
  type ExtractPopulation,
} from '../services/reportingApi';
import type { DatasetManifestEntry } from '../types/reporting';
import {
  currentScopeFor,
  getDatasetVersion,
  subscribeDatasetVersions,
  withDatasets,
} from './duckdbEngine';
import { populationWhereClause } from './compileReport';

// Distinct-value lists power the "is"/"is not" filter combobox. They are small,
// so we cache them per dataset+column+version. The extract IS mutated mid-session
// by a forced Refresh (which re-registers the Parquet and bumps the dataset
// version); folding that version into the cache key rebuilds the list against the
// fresh rows instead of leaving pre-refresh options in the combobox.
const CAP = 200;

interface Distinct {
  values: string[];
  capped: boolean;
}

const cache = new Map<string, Distinct>();
// In-flight loads are shared so two hooks mounted for the same column (e.g. a
// saved report opened with an eq/neq filter) issue a single query instead of
// racing. The completed result is promoted into `cache`; a failure clears the
// entry so a later mount can retry.
const inflight = new Map<string, Promise<Distinct>>();
// withDatasets dedupes concurrent in-flight extract loads engine-side (shared
// with the report runner), so this hook only coordinates its own distinct
// queries via `inflight` below.

// The `cache`/`inflight` keys embed the dataset version, so a forced Refresh that
// bumps the version strands the entire prior generation of keys in both maps for
// the rest of the session — an unbounded leak across repeated refreshes. Drop any
// entry for this dataset whose embedded version no longer matches the current
// one. (`datasetReady` is keyed by bare dataset key — bounded by the dataset
// count — so it needs no sweep.) Called from the hook effect, which re-runs on
// every version bump (the version is folded into its `key` dependency), so stale
// generations are evicted as soon as the dataset re-registers. The
// current-generation entries the invalidation test relies on always survive,
// since their version equals `getDatasetVersion`.
function evictStalePriorGenerations(datasetKey: string): void {
  const current = String(getDatasetVersion(datasetKey));
  for (const map of [cache, inflight]) {
    for (const key of map.keys()) {
      const [entryDataset, , version] = key.split('::');
      if (entryDataset === datasetKey && version !== current) map.delete(key);
    }
  }
}

// Test-only: current `cache` keys, so a regression test can prove a late
// in-flight load never resurrects an already-evicted stale-generation key.
export function getColumnValuesCacheKeysForTests(): string[] {
  return [...cache.keys()];
}

export interface ColumnValues {
  values: string[];
  loading: boolean;
  // True when the distinct list hit the CAP, so the combobox can note that not
  // every value is shown.
  capped: boolean;
}

// Mirror the compiler's identifier quoting so a column/dataset name can't break
// out of its quotes. We also validate the column against the manifest before
// building the query, so this is defense in depth rather than the only guard.
const q = (ident: string): string => `"${ident.replace(/"/g, '""')}"`;

function loadDistinct(
  datasetKey: string,
  column: string,
  key: string,
  population: ExtractPopulation
): Promise<Distinct> {
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);
  let pending = inflight.get(key);
  if (!pending) {
    pending = (async () => {
      // Ensure-and-query in ONE session: the dataset's view holds a single scope
      // at a time, so ensuring outside the lock and querying afterwards could
      // read whichever scope the report runner registered in between.
      //
      // The scope requested is whatever is CURRENTLY registered (FULL_SCOPE if
      // nothing is): asking for a scope of our own would either fight the runner
      // for the one view or pull the entire dataset down just to fill a dropdown.
      // Piggybacking also makes the offered values exactly the values the report
      // can match, and the dataset version — which bumps on every
      // re-registration, scope changes included — re-keys this cache when the
      // window moves.
      const populationWhere = populationWhereClause(datasetKey, population);
      const where = populationWhere
        ? `${q(column)} IS NOT NULL AND ${populationWhere}`
        : `${q(column)} IS NOT NULL`;
      const { rows } = await withDatasets(
        [
          {
            key: datasetKey,
            scope: currentScopeFor(datasetKey),
            population,
          },
        ],
        session =>
          // Fetch one past the cap so a full page signals the list was truncated.
          session.query(
            `SELECT DISTINCT ${q(column)} AS v FROM ${q(datasetKey)} ` +
              `WHERE ${where} ORDER BY 1 LIMIT ${CAP + 1}`
          )
      );
      const all = rows
        .map(row => (row as { v: unknown }).v)
        .filter((v): v is string | number => v !== null && v !== undefined)
        .map(String);
      const capped = all.length > CAP;
      const result: Distinct = {
        values: capped ? all.slice(0, CAP) : all,
        capped,
      };
      // Only promote into `cache` if this key's generation is still current. A
      // slow load started before a forced Refresh resolves AFTER
      // evictStalePriorGenerations already dropped its (now-stale) key; an
      // unconditional set here would resurrect that evicted key and let stale
      // generations accumulate across repeated refreshes. The still-mounted
      // caller receives `result` via the returned promise regardless — the
      // hook's own cancellation guard discards it if the version moved on.
      const [, , keyVersion] = key.split('::');
      if (String(getDatasetVersion(datasetKey)) === keyVersion) {
        cache.set(key, result);
      }
      return result;
    })()
      .catch(error => {
        // Let a later mount retry rather than caching the failure.
        throw error;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, pending);
  }
  return pending;
}

/**
 * Load a column's distinct, non-null values (capped at 200) straight from the
 * already-local DuckDB extract, for use as filter-value options.
 *
 * Takes the full manifest entry rather than a bare key so it can validate that
 * `column` really exists in the dataset before interpolating its name into SQL.
 * When `enabled` is false or the column is unknown, it stays idle and fetches
 * nothing. `population` keeps the distinct values on the same test/inactive
 * population as the active report definition.
 */
export function useColumnValues(
  dataset: DatasetManifestEntry,
  column: string,
  enabled: boolean,
  population: ExtractPopulation = DEFAULT_EXTRACT_POPULATION
): ColumnValues {
  const known = dataset.columns.some(c => c.name === column);
  // Re-render (and re-key the cache) whenever a Refresh re-registers this dataset
  // so an already-open combobox picks up newly added values instead of the stale
  // pre-refresh list.
  const version = useSyncExternalStore(subscribeDatasetVersions, () =>
    getDatasetVersion(dataset.key)
  );
  const key = `${dataset.key}::${column}::${version}::${populationFingerprint(population)}${population.excludeInvoiceClaims ? '|noinvoice' : ''}`;
  const [state, setState] = useState<ColumnValues>(() => {
    const hit = cache.get(key);
    return hit
      ? { values: hit.values, loading: false, capped: hit.capped }
      : { values: [], loading: false, capped: false };
  });

  useEffect(() => {
    // Runs on every version bump (version is folded into `key`), so a forced
    // Refresh's re-registration evicts this dataset's prior-generation cache
    // entries before we consult the cache below — bounding session growth.
    evictStalePriorGenerations(dataset.key);
    if (!enabled || !known) return;
    const hit = cache.get(key);
    if (hit) {
      setState({ values: hit.values, loading: false, capped: hit.capped });
      return;
    }
    let cancelled = false;
    setState({ values: [], loading: true, capped: false });
    loadDistinct(dataset.key, column, key, population)
      .then(result => {
        if (!cancelled)
          setState({
            values: result.values,
            loading: false,
            capped: result.capped,
          });
      })
      .catch(error => {
        // A failed value fetch just means the combobox has no options to offer;
        // the filter still works if the user picks another operator.
        console.error('Failed to load column values', error);
        if (!cancelled) setState({ values: [], loading: false, capped: false });
      });
    return () => {
      cancelled = true;
    };
  }, [
    dataset.key,
    column,
    enabled,
    known,
    key,
    population.includeTestRows,
    population.includeInactiveEpisodes,
    population.includeImportedPatients,
    population.excludeInvoiceClaims,
  ]);

  return state;
}
